/**
 * TagsTour-specific types and step definitions.
 *
 * The TagsTour walks users through tag management — creating a tag in
 * Settings, assigning it to a conversation via the contact details
 * sidebar, and filtering conversations by tag.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each TagsTour step */
export type TagsTourStepId =
  | 'tags_welcome'
  | 'settings_nav'
  | 'settings_tags_tab'
  | 'highlight_tags_management'
  | 'highlight_add_tag_btn'
  | 'highlight_add_tag_form'
  | 'tag_created'
  | 'highlight_contact_panel_btn'
  | 'highlight_tags_section'
  | 'highlight_add_tag_toggle'
  | 'highlight_available_tags'
  | 'highlight_filter_btn'
  | 'highlight_filter_tags'
  | 'tags_complete';

/** Ordered list of all TagsTour steps */
export const TAGS_TOUR_STEPS: TourStep[] = [
  {
    id: 'tags_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'settings_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings"]',
    title: 'Settings',
    content:
      'First, let\'s head to Settings where you can create and manage your tags. Click here to open Settings.',
    placement: 'right',
    advanceOnNavigate: '/settings',
  },
  {
    id: 'settings_tags_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings-nav-tags"]',
    title: 'Tag Management',
    content:
      'Click here to open the Tag Management section where you can create, edit, and organize your tags.',
    placement: 'right',
    advanceOnElement: '[data-tour-id="tags-management"]',
  },
  {
    id: 'highlight_tags_management',
    type: 'highlight',
    targetSelector: '[data-tour-id="tags-management"]',
    title: 'Tag Management',
    content:
      'This is your tag headquarters. Here you can create custom tags to categorize conversations across your agency. Every team member will see these tags when organizing conversations.',
    placement: 'left',
    positionInside: true,
  },
  {
    id: 'highlight_add_tag_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-tag-btn"]',
    title: 'Create a Tag',
    content: 'Click here to create a new tag for your agency.',
    placement: 'left',
    advanceOnElement: '[data-tour-id="add-tag-form"]',
  },
  {
    id: 'highlight_add_tag_form',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-tag-form"]',
    title: 'Create Your Tag',
    content:
      'Give your tag a name and pick a color. Tags help you categorize conversations — for example, "New Lead," "Follow Up," or "VIP Client." When you\'re done, hit "Add Tag" to save it.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="add-tag-form"]',
  },
  {
    id: 'tag_created',
    type: 'highlight',
    targetSelector: '[data-tour-id="tags-management"]',
    title: 'Tag Created!',
    content:
      'Your new tag is ready to use. Next, we\'ll show you how to assign tags to conversations so you can start organizing your inbox.',
    placement: 'left',
    positionInside: true,
  },
  {
    id: 'highlight_contact_panel_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="contact-panel-btn"]',
    title: 'Contact Details',
    content:
      'Now let\'s see how to assign a tag to a conversation. Click here to open the contact details panel.',
    placement: 'left',
    advanceOnElement: '[data-tour-id="contact-sidebar-open"]',
  },
  {
    id: 'highlight_tags_section',
    type: 'highlight',
    targetSelector: '[data-tour-id="tags-section"]',
    title: 'Conversation Tags',
    content:
      'This is where you can see and manage the tags assigned to this conversation. Tags you create in Settings will appear here for quick assignment.',
    placement: 'left',
  },
  {
    id: 'highlight_add_tag_toggle',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-tag-toggle"]',
    title: 'Add a Tag',
    content:
      'Click this button to reveal all available tags. You can then select one to assign it to this conversation.',
    placement: 'left',
    advanceOnElement: '[data-tour-id="available-tags"]',
  },
  {
    id: 'highlight_available_tags',
    type: 'highlight',
    targetSelector: '[data-tour-id="available-tags"]',
    title: 'Select a Tag',
    content:
      'Here are your available tags. Click any tag to assign it to this conversation. You can assign multiple tags to keep things organized.',
    placement: 'left',
    allowInteraction: true,
  },
  {
    id: 'highlight_filter_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="filter-btn"]',
    title: 'Filter by Tags',
    content:
      'Now let\'s see how tags help you find conversations. Click the Sort & Filter button to open the filter options.',
    placement: 'right',
    advanceOnElement: '[data-tour-id="filter-dropdown"]',
  },
  {
    id: 'highlight_filter_tags',
    type: 'highlight',
    targetSelector: '[data-tour-id="filter-tags-section"]',
    title: 'Filter by Tag',
    content:
      'Under "Tags," you can filter your conversation list by any tag. This is a powerful way to quickly find conversations in a specific category — like viewing all "Follow Up" conversations or focusing on "VIP Clients."',
    placement: 'right',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="filter-dropdown"]',
  },
  {
    id: 'tags_complete',
    type: 'modal',
    title: 'Tags Tour Complete',
    content:
      'You now know how to create tags, assign them to conversations, and filter your inbox to stay organized.',
  },
];
